import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';
import { LOGO_ICON, LOGO_REVERSE } from '@/hooks/useThemedLogo';
import { usePortalSidebarStore } from '@/store/portalSidebar.store';
import { usePortalDashboard } from '@/hooks/usePortal';
import {
  LayoutDashboard, ClipboardList, Swords, Bell, UserCircle, LogOut,
  PanelLeftClose, PanelLeftOpen, ChevronRight, ChevronDown, Globe, Menu, ShieldCheck, Users,
} from 'lucide-react';
import clsx from 'clsx';
import { humanize } from '@/utils/labels';

const PORTAL_NAV_ITEMS = [
  { to: '/portal/dashboard',     label: 'Dashboard',          icon: LayoutDashboard, key: 'dashboard'     as const },
  { to: '/portal/engagements',   label: 'Engagements',        icon: ClipboardList,   key: 'engagements'   as const },
  { to: '/portal/exercises',     label: 'Tabletop Exercises', icon: Swords,          key: 'exercises'     as const },
  { to: '/portal/trust-centre',  label: 'Trust Centre',       icon: ShieldCheck,     key: 'trustCentre'   as const },
  { to: '/portal/users',         label: 'Users',              icon: Users,           key: 'users'         as const },
  { to: '/portal/notifications', label: 'Notifications',      icon: Bell,            key: 'notifications' as const },
  { to: '/portal/profile',       label: 'My Profile',         icon: UserCircle,      key: 'profile'       as const },
];

/* ── Reusable NavItem (mirrors admin sidebar pattern) ────────────── */
function PortalNavItem({
  to, label, icon: Icon, collapsed,
}: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; collapsed: boolean }) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center rounded text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2',
          isActive
            // Selected: navy row, ink-d label and a 3px orange bar on the left.
            ? 'bg-gray-100 text-gray-900 before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:bg-dl-orange'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={clsx('h-5 w-5 shrink-0', isActive ? 'text-dl-orange' : 'text-gray-400 group-hover:text-gray-700')} />
          {!collapsed && (
            <>
              <span className="truncate">{label}</span>
              {isActive && <ChevronRight className="ml-auto h-4 w-4 text-gray-500" />}
            </>
          )}
          {collapsed && (
            <span className="pointer-events-none absolute left-14 z-50 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
              {label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function PortalLayout() {
  const navigate = useNavigate();
  const { user, logout, clientIp } = useAuthStore();
  const { appName, copyrightText } = useBrandingStore();
  const { collapsed, toggle, mobileOpen, closeMobile, openMobile } = usePortalSidebarStore();
  const { data: dashboard } = usePortalDashboard();
  const showEngagements = dashboard?.organisation?.showPortalEngagements !== false;
  const showExercises   = dashboard?.organisation?.showPortalExercises   !== false;
  // Notifications tab is hidden by default — admins enable it from Notification Settings.
  const showNotifications = dashboard?.portalNotificationsEnabled === true;
  // Only when a pack has actually been granted to this organisation.
  const showTrustCentre   = dashboard?.portalTrustCentreEnabled === true;
  const portalNav = PORTAL_NAV_ITEMS.filter(item => {
    if (item.key === 'engagements')   return showEngagements;
    if (item.key === 'exercises')     return showExercises;
    if (item.key === 'trustCentre')   return showTrustCentre;
    if (item.key === 'notifications') return showNotifications;
    // Managing colleagues is an administrator's job, not every user's.
    if (item.key === 'users')         return user?.role === 'CLIENT_ADMIN';
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';

  const year = new Date().getFullYear();
  const copyright = copyrightText || `\u00a9 ${year} ${appName}. All rights reserved.`;

  return (
    <div className="flex h-screen overflow-hidden bg-surface-2">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={closeMobile}
          aria-hidden
        />
      )}
      {/* ── Sidebar ───────────────────────────────────────────────── */}
      <aside
        onClick={(e) => {
          if (mobileOpen && (e.target as HTMLElement).closest('a')) closeMobile();
        }}
        className={clsx(
          'chrome flex flex-col border-r border-gray-200 bg-surface transition-[width,transform] duration-200 ease-in-out shrink-0',
          'fixed inset-y-0 left-0 z-40 w-64 md:static md:translate-x-0',
          mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
          collapsed ? 'md:w-16' : 'md:w-64',
        )}
      >
        {/* Logo + toggle */}
        <div
          className={clsx(
            'flex h-16 items-center justify-between shrink-0 border-b border-gray-200',
            collapsed ? 'flex-col justify-center gap-1 px-2' : 'px-4',
          )}
        >
          {/* Hull chrome: the reverse logo, or the square icon when collapsed. */}
          <img
            src={collapsed ? LOGO_ICON : LOGO_REVERSE}
            alt={appName}
            className={clsx('object-contain shrink-0', collapsed ? 'h-7 w-7 rounded-sm' : 'h-9 w-auto max-w-[150px]')}
          />
          <button
            onClick={toggle}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors shrink-0"
          >
            {collapsed
              ? <PanelLeftOpen  className="h-4 w-4" />
              : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        {/* Org info (expanded only) */}
        {user && !collapsed && (
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-xs text-gray-400">Logged in as</p>
            <p className="text-sm font-medium text-gray-800 truncate">{user.firstName} {user.lastName}</p>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
          <div className="space-y-0.5">
            {portalNav.map(item => (
              <PortalNavItem {...item} collapsed={collapsed} />
            ))}
          </div>
        </nav>

        {/* IP + Logout */}
        <div className="border-t border-gray-200 p-2 shrink-0 space-y-2">
          {/* Logged-in-from box */}
          {clientIp && !collapsed && (
            <div className="mx-1 rounded-lg border border-gray-200 px-3 py-2">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Globe className="h-3.5 w-3.5 shrink-0" />
                Logged in from
              </div>
              <p className="mt-0.5 text-xs font-medium text-gray-600 truncate">{clientIp}</p>
            </div>
          )}
          {clientIp && collapsed && (
            <div
              title={`Logged in from: ${clientIp}`}
              className="flex justify-center py-1"
            >
              <Globe className="h-4 w-4 text-gray-400" />
            </div>
          )}
          <button
            onClick={handleLogout}
            title={collapsed ? 'Sign Out' : undefined}
            className={clsx(
              'flex w-full items-center rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all',
              collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2',
            )}
          >
            <LogOut className="h-5 w-5 text-gray-400 shrink-0" />
            {!collapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* ── Right column (header + content + footer) ──────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="chrome flex h-16 items-center justify-between border-b border-gray-200 px-3 sm:px-6 shrink-0 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={openMobile}
              className="md:hidden rounded p-1.5 text-white/85 hover:bg-white/15"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="text-sm font-medium text-white/80 truncate">
              {appName || 'GRC'} &mdash; Client Portal
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <NavLink
              to="/portal/profile"
              className="flex items-center gap-2 rounded-lg bg-white/15 px-2 sm:px-3 py-1.5 transition hover:bg-white/25"
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-dl-orange text-xs font-semibold text-dl-orange-on shrink-0">
                {initials}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-white truncate max-w-[140px]">
                {user ? `${user.firstName} ${user.lastName}` : 'User'}
              </span>
              <span className="hidden md:inline rounded bg-white/20 px-2 py-0.5 ap-label text-white">
                {humanize(user?.role)}
              </span>
              <ChevronDown className="hidden sm:inline h-3.5 w-3.5 text-white/70" />
            </NavLink>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 rounded-lg px-2 sm:px-3 py-1.5 text-sm text-white/80 transition hover:bg-white/15 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 bg-surface-2">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="flex h-10 shrink-0 items-center justify-between border-t border-gray-200 bg-surface px-3 sm:px-6">
          <p className="text-xs text-gray-400 truncate">{copyright}</p>
        </footer>
      </div>
    </div>
  );
}
